import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Globe, 
  Instagram, 
  Youtube, 
  Facebook, 
  Twitter, 
  Linkedin,
  Share2,
  Save,
  Edit,
  Trash2,
  Plus,
  ExternalLink,
  Check,
  X,
  MessageCircle,
  Mail,
  AlertTriangle
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';
import { ConfirmacionList } from './Confirmaciones/ConfirmacionList';
import { AvisoList } from './Avisos/AvisoList';

type TabType = 'social' | 'confirmaciones' | 'avisos';

interface SocialLink {
  id: string;
  platform: string;
  url: string;
  icon: React.ReactNode;
  active: boolean;
}

interface SocialData {
  website: string;
  socialLinks: SocialLink[];
}

const DEFAULT_DATA: SocialData = {
  website: 'www.mitecnohogar.com.ar',
  socialLinks: [
    {
      id: '1',
      platform: 'Instagram',
      url: 'https://instagram.com/mitecnohogar',
      icon: <Instagram size={20} />,
      active: true,
    },
    {
      id: '2',
      platform: 'Facebook',
      url: 'https://facebook.com/mitecnohogar',
      icon: <Facebook size={20} />,
      active: true,
    },
    {
      id: '3',
      platform: 'YouTube',
      url: 'https://youtube.com/@mitecnohogar',
      icon: <Youtube size={20} />,
      active: false,
    },
  ],
};

const platforms = [
  { id: 'instagram', label: 'Instagram', icon: <Instagram size={18} />, color: 'text-pink-500' },
  { id: 'facebook', label: 'Facebook', icon: <Facebook size={18} />, color: 'text-blue-600' },
  { id: 'youtube', label: 'YouTube', icon: <Youtube size={18} />, color: 'text-red-600' },
  { id: 'twitter', label: 'Twitter/X', icon: <Twitter size={18} />, color: 'text-gray-700' },
  { id: 'linkedin', label: 'LinkedIn', icon: <Linkedin size={18} />, color: 'text-blue-700' },
  { id: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle size={18} />, color: 'text-green-500' },
];

export function Social() {
  const location = useLocation();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [editingLink, setEditingLink] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPlatform, setNewPlatform] = useState('');
  const [newUrl, setNewUrl] = useState('');
  
  const initialState = location.state as { tab?: 'social' | 'confirmaciones' | 'avisos'; envioId?: number } | null;
  
  const [activeTab, setActiveTab] = useState<TabType>(
    initialState?.tab === 'confirmaciones' ? 'confirmaciones' : 
    initialState?.tab === 'avisos' ? 'avisos' : 
    'social'
  );

  const [socialData, setSocialData] = useState<SocialData>(() => {
    const saved = localStorage.getItem('socialData');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          socialLinks: parsed.socialLinks.map((link: any) => ({
            ...link,
            icon: getIconForPlatform(link.platform),
          })),
        };
      } catch (e) {
        console.error('Error al cargar datos guardados:', e);
      }
    }
    return DEFAULT_DATA;
  });

  useEffect(() => {
    const dataToSave = {
      website: socialData.website,
      socialLinks: socialData.socialLinks.map(({ id, platform, url, active }) => ({
        id,
        platform,
        url,
        active,
      })),
    };
    localStorage.setItem('socialData', JSON.stringify(dataToSave));
  }, [socialData]);

  function getIconForPlatform(platform: string): React.ReactNode {
    const platformMap: Record<string, React.ReactNode> = {
      'Instagram': <Instagram size={20} />,
      'Facebook': <Facebook size={20} />,
      'YouTube': <Youtube size={20} />,
      'Twitter/X': <Twitter size={20} />,
      'LinkedIn': <Linkedin size={20} />,
      'WhatsApp': <MessageCircle size={20} />,
    };
    return platformMap[platform] || <Share2 size={20} />;
  }

  const handleSaveWebsite = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success('Sitio web actualizado correctamente');
    } catch (error) {
      toast.error('Error al actualizar el sitio web');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLink = () => {
    if (!newPlatform || !newUrl) {
      toast.error('Completa todos los campos');
      return;
    }

    const platformData = platforms.find(p => p.id === newPlatform);
    if (!platformData) return;

    const newLink: SocialLink = {
      id: Date.now().toString(),
      platform: platformData.label,
      url: newUrl,
      icon: platformData.icon,
      active: true,
    };

    setSocialData({
      ...socialData,
      socialLinks: [...socialData.socialLinks, newLink],
    });

    setNewPlatform('');
    setNewUrl('');
    setShowAddForm(false);
    toast.success('Red social agregada correctamente');
  };

  const handleToggleActive = (id: string) => {
    setSocialData({
      ...socialData,
      socialLinks: socialData.socialLinks.map(link =>
        link.id === id ? { ...link, active: !link.active } : link
      ),
    });
  };

  const handleDeleteLink = (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta red social?')) return;
    setSocialData({
      ...socialData,
      socialLinks: socialData.socialLinks.filter(link => link.id !== id),
    });
    toast.success('Red social eliminada');
  };

  const handleEditLink = (id: string) => {
    setEditingLink(editingLink === id ? null : id);
  };

  const handleUpdateLink = (id: string, newUrl: string) => {
    setSocialData({
      ...socialData,
      socialLinks: socialData.socialLinks.map(link =>
        link.id === id ? { ...link, url: newUrl } : link
      ),
    });
    setEditingLink(null);
    toast.success('URL actualizada');
  };

  const handleReset = () => {
    if (!confirm('¿Restaurar los datos por defecto? Se perderán todos los cambios.')) return;
    localStorage.removeItem('socialData');
    setSocialData(DEFAULT_DATA);
    toast.success('Datos restaurados');
  };

  const getPlatformInfo = (platformName: string) => {
    return platforms.find(p => p.label === platformName) || platforms[0];
  };

  const renderTabs = () => (
    <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-6">
      <button
        onClick={() => setActiveTab('social')}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
          activeTab === 'social'
            ? 'text-blue-600 border-b-2 border-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <Share2 size={16} />
        Redes Sociales
      </button>
      <button
        onClick={() => setActiveTab('confirmaciones')}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
          activeTab === 'confirmaciones'
            ? 'text-blue-600 border-b-2 border-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <Mail size={16} />
        Confirmaciones
      </button>
      <button
        onClick={() => setActiveTab('avisos')}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
          activeTab === 'avisos'
            ? 'text-blue-600 border-b-2 border-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <AlertTriangle size={16} />
        Avisos
      </button>
    </div>
  );

  const renderSocial = () => (
    <>
      {/* Sitio Web */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 rounded-full">
            <Globe className="text-blue-600" size={24} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-800">Sitio Web</h2>
            <p className="text-sm text-gray-500">Tu página web o tienda online</p>
            <div className="flex gap-3 mt-3">
              <Input
                value={socialData.website}
                onChange={(e) => setSocialData({ ...socialData, website: e.target.value })}
                placeholder="www.tusitio.com"
                className="flex-1 max-w-md"
              />
              <Button
                onClick={handleSaveWebsite}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Save size={16} />
                Guardar
              </Button>
            </div>
            {socialData.website && (
              <a
                href={`https://${socialData.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-2"
              >
                Visitar sitio web
                <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </Card>

      {/* Redes Sociales */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-50 rounded-full">
              <Share2 className="text-purple-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Redes Sociales</h2>
              <p className="text-sm text-gray-500">
                {socialData.socialLinks.filter(l => l.active).length} redes activas de {socialData.socialLinks.length}
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Plus size={18} />
            Agregar Red Social
          </Button>
        </div>

        {/* Formulario para agregar */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3">Agregar nueva red social</p>
            <div className="flex flex-wrap gap-3">
              <select
                value={newPlatform}
                onChange={(e) => setNewPlatform(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="">Seleccionar plataforma...</option>
                {platforms.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <Input
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1 min-w-[200px]"
              />
              <Button
                onClick={handleAddLink}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Check size={16} />
                Agregar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setNewPlatform('');
                  setNewUrl('');
                }}
              >
                <X size={16} />
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Lista de redes sociales */}
        <div className="space-y-3">
          {socialData.socialLinks.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Share2 size={48} className="mx-auto mb-3 text-gray-300" />
              <p>No hay redes sociales agregadas</p>
              <p className="text-sm">Agrega tus redes para mostrarlas en tu negocio</p>
            </div>
          ) : (
            socialData.socialLinks.map((link) => {
              const platformInfo = getPlatformInfo(link.platform);
              return (
                <div
                  key={link.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    link.active
                      ? 'border-gray-200 bg-white hover:shadow-sm'
                      : 'border-gray-100 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`${platformInfo.color}`}>
                      {link.icon}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{link.platform}</p>
                      {editingLink === link.id ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            value={link.url}
                            onChange={(e) => {
                              const updatedLinks = socialData.socialLinks.map(l =>
                                l.id === link.id ? { ...l, url: e.target.value } : l
                              );
                              setSocialData({ ...socialData, socialLinks: updatedLinks });
                            }}
                            className="min-w-[250px]"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateLink(link.id, link.url)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingLink(null)}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          {link.url}
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(link.id)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        link.active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {link.active ? 'Activo' : 'Inactivo'}
                    </button>
                    <button
                      onClick={() => handleEditLink(link.id)}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteLink(link.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Vista previa */}
      <Card className="p-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Vista previa de tus redes</h3>
        <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
          {socialData.website && (
            <a
              href={`https://${socialData.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <Globe size={18} className="text-blue-600" />
              <span className="text-sm font-medium">Web</span>
            </a>
          )}
          {socialData.socialLinks.filter(l => l.active).map((link) => {
            const platformInfo = getPlatformInfo(link.platform);
            return (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <span className={platformInfo.color}>{link.icon}</span>
                <span className="text-sm font-medium">{link.platform}</span>
              </a>
            );
          })}
          {socialData.socialLinks.filter(l => l.active).length === 0 && !socialData.website && (
            <p className="text-sm text-gray-400">No hay redes sociales activas para mostrar</p>
          )}
        </div>
      </Card>
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Social</h1>
          <p className="text-sm text-gray-500">
            Administra tus redes sociales y confirmaciones de compra
          </p>
        </div>
        {activeTab === 'social' && (
          <Button
            variant="outline"
            onClick={handleReset}
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            Restaurar por defecto
          </Button>
        )}
      </div>

      {renderTabs()}

      {activeTab === 'social' ? renderSocial() : activeTab === 'confirmaciones' ? <ConfirmacionList /> : <AvisoList />}

      <div className="text-xs text-gray-400 border-t border-gray-200 pt-4 flex justify-between">
        <p>Usuario: <span className="font-medium">{user?.usuario || 'No definido'}</span></p>
        <p>Los datos se guardan automáticamente en tu navegador</p>
      </div>
    </div>
  );
}